import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Cpu, Shield, Volume2, Wifi } from 'lucide-react';
import { getCatalogV2Item } from '../api';
import type { CatalogV2ItemResponse } from '../types';
import { useHaptic } from '../hooks/useHaptic';
import { PROTOCOL_THEME } from '../protocolTheme';

export default function CatalogItemPage() {
  const [searchParams] = useSearchParams();
  const article = searchParams.get('article');
  const navigate = useNavigate();
  const haptic = useHaptic();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CatalogV2ItemResponse | null>(null);

  useEffect(() => {
    if (!article) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getCatalogV2Item(article)
      .then((resp) => setData(resp))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [article]);

  if (loading) {
    return (
      <div className="p-4">
        <div className="rounded-2xl p-4 animate-pulse" style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-glass)' }}>
          <div className="h-5 w-1/2 rounded bg-white/10 mb-3" />
          <div className="h-4 w-full rounded bg-white/10 mb-2" />
          <div className="h-4 w-5/6 rounded bg-white/10" />
        </div>
      </div>
    );
  }

  if (!data || !article) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <p className="text-sm" style={{ color: 'var(--text-2)' }}>Позиция не найдена в витрине</p>
        <button className="text-sm font-semibold" style={{ color: 'var(--cyan)' }} onClick={() => navigate('/catalog')}>
          Вернуться в премиум-каталог
        </button>
      </div>
    );
  }

  const { item } = data;

  return (
    <div className="p-4 pb-24 space-y-3">
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            haptic.selection();
            navigate('/catalog');
          }}
          className="press p-2 rounded-xl"
          style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid var(--border-glass)' }}
        >
          <ArrowLeft size={18} color="var(--cyan-bright)" />
        </button>
        <div>
          <p className="text-xs" style={{ color: 'var(--text-3)' }}>
            {data.category} • {data.subcategory}
          </p>
          <h1 className="text-base font-bold" style={{ color: 'var(--text-1)' }}>
            {item.name}
          </h1>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-3)' }}>
            Позиция из актуальной инженерной витрины Onviz
          </p>
        </div>
      </div>

      <div className="rounded-2xl p-4" style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-glass)' }}>
        <p className="text-xs mb-2" style={{ color: 'var(--text-3)' }}>
          {item.article} • {data.line} • {data.motorType} • {data.controlType}
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {[...item.specs.communication, data.controlType].map((proto) => {
            const theme = PROTOCOL_THEME[proto];
            return (
              <span
                key={proto}
                className="text-[11px] font-semibold px-2 py-1 rounded-full"
                style={theme ? {
                  color: theme.text,
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                } : {
                  color: 'var(--text-2)',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                {theme ? `${theme.icon} ${theme.label}` : proto}
              </span>
            );
          })}
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
          {item.description}
        </p>
        <p className="text-lg font-black mt-3" style={{ color: 'var(--cyan-bright)' }}>
          от {item.price.fromRub.toLocaleString('ru-RU')} ₽
        </p>
        <p className="text-[10px] mt-1" style={{ color: 'var(--text-3)' }}>
          Итоговая комплектация и стоимость подтверждаются после уточнения параметров проекта.
        </p>
      </div>

      <div className="rounded-2xl p-4" style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-glass)' }}>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-1)' }}>Ключевые преимущества решения</p>
        <div className="flex flex-wrap gap-2">
          {item.features.map((feature) => (
            <span
              key={feature}
              className="text-xs px-2 py-1 rounded-full"
              style={{ color: 'var(--text-2)', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-subtle)' }}
            >
              {feature}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-2xl p-4 space-y-2" style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-glass)' }}>
        <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-1)' }}>Технический профиль</p>
        <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
          <Cpu size={14} /> Мощность: {item.specs.powerW}W
        </p>
        <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
          <Volume2 size={14} /> Уровень шума: до {item.specs.noiseDbMax} дБ
        </p>
        <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
          <Shield size={14} /> Защита: {item.specs.ipClass}
        </p>
        <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
          <Wifi size={14} /> Протоколы: {item.specs.communication.join(', ')}
        </p>
      </div>
    </div>
  );
}
